import { childLocalId } from "../tree/id.js";
import { describeHandler } from "../inspector.js";
import { composeHandlers } from "./composed.js";
import { focusHandler } from "./focus.js";
import { parentHandler } from "./handler.js";
//#region src/handler/directional.ts
/**
* Building block for {@link verticalHandler}.
* Handles `move` actions in up and down directions by focusing the previous or next child respectively.
*
* This is a primitive handler and as such is meant to be used as part of a composed handler.
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#directional-movement-handlers}
* @see {@link NavigationHandler}
*/
function verticalMovementHandler(action, ctx) {
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeHandler(action, { name: "vertical-movement" });
	if (action.kind !== "move") return ctx.next();
	if (action.direction === "up" || action.direction === "backwards") return findPreviousChild(ctx, "up") ?? ctx.next();
	if (action.direction === "down" || action.direction === "forwards") return findNextChild(ctx, "down") ?? ctx.next();
	return ctx.next();
}
function verticalFocusDirection(dir) {
	if (dir === "up" || dir === "backwards") return "backwards";
	if (dir === "down" || dir === "forwards") return "forwards";
	return null;
}
/**
* Navigation handler that handles movement in up and down directions
* by focusing the previous or next child respectively.
*
* @example
*
* ```ts
* const nav = useNavnode("list", verticalHandler);
* ```
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#vertical-handler}
* @see {@link ComposedHandler}
*/
const verticalHandler = composeHandlers([
	focusHandler({
		focusWhenEmpty: false,
		direction: verticalFocusDirection
	}),
	verticalMovementHandler,
	parentHandler
]);
/**
* Building block for {@link horizontalHandler}.
* Handles `move` actions in left and right directions by focusing the previous or next child respectively.
*
* This is a primitive handler and as such is meant to be used as part of a composed handler.
*
* @see {@link NavigationHandler}
* @see {@link https://fiveway.dev/guide/built-in-handlers#directional-movement-handlers}
*/
function horizontalMovementHandler(action, ctx) {
	if (import.meta.env.FIVEWAY_INSPECTOR ?? import.meta.env.DEV) describeHandler(action, { name: "horizontal-movement" });
	if (action.kind !== "move") return ctx.next();
	if (action.direction === "left" || action.direction === "backwards") return findPreviousChild(ctx, "left") ?? ctx.next();
	if (action.direction === "right" || action.direction === "forwards") return findNextChild(ctx, "right") ?? ctx.next();
	return ctx.next();
}
function horizontalFocusDirection(dir) {
	if (dir === "left" || dir === "backwards") return "backwards";
	if (dir === "right" || dir === "forwards") return "forwards";
	return null;
}
/**
* Navigation handler that handles movement in left and right directions
* by focusing the previous or next child respectively.
*
* @example
*
* ```ts
* const nav = useNavnode("list", horizontalHandler);
* ```
*
* @see {@link https://fiveway.dev/guide/built-in-handlers#horizontal-handler}
* @see {@link ComposedHandler}
*/
const horizontalHandler = composeHandlers([
	focusHandler({
		focusWhenEmpty: false,
		direction: horizontalFocusDirection
	}),
	horizontalMovementHandler,
	parentHandler
]);
function findNextChild(ctx, direction) {
	const currentChildId = childLocalId(ctx.node.id, ctx.node.tree.focus);
	if (currentChildId === null) return null;
	const currentIndex = ctx.node.children.findIndex((c) => c.id === currentChildId);
	for (let i = currentIndex + 1; i < ctx.node.children.length; i++) {
		const child = ctx.node.children[i];
		if (!child.active) continue;
		const nextId = ctx.next(child.id, {
			kind: "focus",
			direction
		});
		if (nextId !== null) return nextId;
	}
	return null;
}
function findPreviousChild(ctx, direction) {
	const currentChildId = childLocalId(ctx.node.id, ctx.node.tree.focus);
	if (currentChildId === null) return null;
	const currentIndex = ctx.node.children.findIndex((c) => c.id === currentChildId);
	for (let i = currentIndex - 1; i >= 0; i--) {
		const child = ctx.node.children[i];
		if (!child.active) continue;
		const nextId = ctx.next(child.id, {
			kind: "focus",
			direction
		});
		if (nextId !== null) return nextId;
	}
	return null;
}
//#endregion
export { horizontalHandler, horizontalMovementHandler, verticalHandler, verticalMovementHandler };

//# sourceMappingURL=directional.js.map